import { DateInput, EventInput } from '@fullcalendar/core'
import { minutesToMs } from '@thyme/libs/src/time/convert'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/legacy/store/modules/notification'
import { useScheduleApi } from '@/legacy/store/modules/page/schedule'
import { NotificationType } from '@/legacy/types/notifications'
import { useWhoAmI } from '@/store/profile/whoAmI'
import { useNavBarItems } from './items'

// Constants
const CHECK_INTERVAL_MINUTES = 30
const UPCOMING_CALL_WINDOW = 31 // minutes to look ahead for upcoming calls, set to 31 because filter_time_before isn't inclusive

// Calculated notification check interval
const NOTIFICATION_CHECK_INTERVAL = minutesToMs(CHECK_INTERVAL_MINUTES) // 30 minutes in milliseconds
const LAST_NOTIFICATION_ID_LOCAL_STORAGE_KEY =
  'last-scheduled-call-notification-id'

/**
 *
 */
export function setup() {
  const router = useRouter()
  const { notification, persistentNotification } = storeToRefs(
    useNotificationStore()
  )
  const scheduleApi = useScheduleApi()
  const { data: scheduleData } = storeToRefs(scheduleApi)
  const notificationStore = useNotificationStore()
  const { selfEntity } = useWhoAmI()
  let checkInterval: number | null = null
  // Store the current call's information for navigation
  const currentCallInfo = ref<{ commId: string; patientId: string } | null>(
    null
  )
  const { items } = useNavBarItems()

  const convertTimeToString = (
    time?: DateInput,
    defaultVal: string | null = null
  ): string | null => {
    if (!time) return null
    return typeof time === 'string'
      ? time
      : time instanceof Date
      ? time.toISOString()
      : defaultVal
  }

  // Function to find the next scheduled call within UPCOMING_CALL_WINDOW, if any
  const findUpcomingCallToNotify = (
    events: EventInput[]
  ): EventInput | null => {
    const now = DateTime.now()

    const upcomingCalls = events
      .filter((event) => {
        if (!event.start) return false
        const startStr = convertTimeToString(event.start, null)
        if (!startStr) return false

        const startTime = DateTime.fromISO(startStr)
        // Check if call is within the upcoming call window to notify
        if (
          startTime > now &&
          startTime <= now.plus({ minutes: UPCOMING_CALL_WINDOW })
        ) {
          // if the call is in the call window check if we've already notified for it
          const lastNotification =
            localStorage.getItem(LAST_NOTIFICATION_ID_LOCAL_STORAGE_KEY) ?? ''
          return event.id !== lastNotification
        } else {
          // if call not in window to notify, exclude
          return false
        }
      })
      .sort((a, b) => {
        const aStartStr = convertTimeToString(a.start, '')
        const bStartStr = convertTimeToString(b.start, '')
        if (!aStartStr || !bStartStr) return 0

        const aStart = DateTime.fromISO(aStartStr)
        const bStart = DateTime.fromISO(bStartStr)
        return aStart.toMillis() - bStart.toMillis()
      })

    return upcomingCalls.length > 0 ? upcomingCalls[0] : null
  }

  // Handle notification click to navigate to the scheduled call
  const handleNotificationClick = () => {
    if (currentCallInfo.value) {
      const { commId, patientId } = currentCallInfo.value
      const routeData = router.resolve({
        name: '#patientProfile',
        params: { patientId },
        query: { commId },
      })
      window.open(routeData.href, '_blank')
    }
  }

  // Function to update the notification
  const updateCallNotification = async () => {
    if (!selfEntity.value?.entityId) return

    try {
      await scheduleApi.listAll({
        params: {
          filter_staff_ids: [selfEntity.value.entityId],
          filter_time_after: DateTime.now().toISO(),
          filter_time_before: DateTime.now()
            .plus({ minutes: UPCOMING_CALL_WINDOW })
            .toISO(),
        },
      })

      const nextCall = findUpcomingCallToNotify(scheduleData.value ?? [])

      if (nextCall?.start) {
        const startStr = convertTimeToString(nextCall.start)

        if (startStr) {
          const startTime = DateTime.fromISO(startStr)
          const minutesUntilCall = Math.round(
            startTime.diff(DateTime.now(), 'minutes').minutes
          )
          const memberName =
            nextCall.title?.replace('Call with ', '') ?? 'Unknown Member'

          // Store the current call information for navigation
          currentCallInfo.value = {
            commId: nextCall.id as string,
            patientId: nextCall.extendedProps?.patientId as string,
          }

          localStorage.setItem(
            LAST_NOTIFICATION_ID_LOCAL_STORAGE_KEY,
            nextCall.id ?? ''
          )

          notificationStore.setPersistentNotification({
            message: `Scheduled call with ${memberName} in ${minutesUntilCall} minutes`,
            type: NotificationType.INFO,
          })
        }
      } else {
        // Clear the call info if no upcoming calls
        currentCallInfo.value = null
      }
    } catch (err) {
      console.error('Error updating call notification:', err)
    }
  }

  const performPeriodicCheck = async () => {
    await updateCallNotification()
  }

  // Set up interval to check for upcoming calls
  onMounted(async () => {
    try {
      // Initial check
      await updateCallNotification()

      // Set up periodic checks
      checkInterval = window.setInterval(
        performPeriodicCheck,
        NOTIFICATION_CHECK_INTERVAL
      )
    } catch (err) {
      console.error('Error in initial call notification check:', err)
    }
  })

  // Clean up interval on component unmount
  onUnmounted(() => {
    if (checkInterval !== null) {
      clearInterval(checkInterval)
    }
  })

  return {
    notification,
    persistentNotification,
    items,
    handleNotificationClick,
  }
}
